import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

import heroImage from "@/assets/ibiza-esvedra-hero.webp";
import logoFigure from "@/assets/logo-arzani-figure.webp";
import introImage from "@/assets/ibiza-intro.webp";
import yachtsHero from "@/assets/ibiza-yachts-hero.webp";
import residencesHero from "@/assets/ibiza-residences-hero.webp";
import driveHero from "@/assets/ibiza-drive-hero.webp";
import lifestyleHero from "@/assets/ibiza-lifestyle-hero.webp";
import nightsHero from "@/assets/ibiza-nights-hero.webp";

import yachtKumbra from "@/assets/yacht-kumbra.webp";
import yachtDeantonio from "@/assets/yacht-deantonio.webp";
import yachtVanquish from "@/assets/yacht-vanquish.webp";
import yachtPardo from "@/assets/yacht-pardo.webp";
import yachtMangusta from "@/assets/yacht-mangusta.webp";

import villaCanchi from "@/assets/villa-canchi.webp";
import villaElephante from "@/assets/villa-elephante.webp";
import villaCeleste from "@/assets/villa-celeste.webp";

import carDefender from "@/assets/car-defender.webp";
import carFerrari from "@/assets/car-ferrari.webp";
import carBentley from "@/assets/car-bentley.webp";
import carPorsche from "@/assets/car-porsche.webp";

import ctaImage from "@/assets/ibiza-cta.webp";

const ease = [0.22, 1, 0.36, 1] as const;

/* ─── DATA ─── */

const services = [
  { title: "Arzani Yachts", description: "Explore Ibiza's coastline aboard exceptional yachts, with every detail arranged to deliver comfort, privacy, and a truly effortless day at sea.", image: yachtsHero, cta: "Explore our fleet", sectionId: "yachts" },
  { title: "Arzani Residences", description: "Discover refined residences across Ibiza, offering privacy, premium locations, and thoughtfully arranged stays tailored entirely to your preferences.", image: residencesHero, cta: "Explore our residences", sectionId: "residences" },
  { title: "Arzani Drive", description: "Travel Ibiza in prestige vehicles delivered directly to your location, combining comfort, flexibility, and seamless mobility throughout your stay.", image: driveHero, cta: "Discover our fleet", sectionId: "drive" },
  { title: "Arzani Lifestyle", description: "Experience Ibiza effortlessly through chauffeur service, VIP transfers, private massages, wellness treatments, and carefully arranged reservations at exclusive restaurants and clubs.", image: lifestyleHero, cta: "Discover Arzani Lifestyle", sectionId: "lifestyle" },
  { title: "Arzani Nights", description: "Enjoy Ibiza after dark with priority guest lists, exclusive table reservations, and chauffeur-driven transport to the island's most iconic venues.", image: nightsHero, cta: "Explore Arzani Nights", sectionId: "nights" },
];

const yachts = [
  { name: "Kumbra 34 'Enjoy'", price: "From €1.000/day", image: yachtKumbra, slug: "kumbra-34-enjoy", specs: ["Length: 10,3 m", "Guests: 11", "Cabins: 1", "Captain: Included", "Port: Marina Botafoch"] },
  { name: "De Antonio D42 'Playyacht III'", price: "From €1.300/day", image: yachtDeantonio, slug: "de-antonio-d42", specs: ["Length: 12.6 m", "Guests: 11", "Cabins: 2", "Captain: Included", "Port: Marina Botafoch"] },
  { name: "Vanquish VQ45", price: "From €2.500/day", image: yachtVanquish, slug: "vanquish-43", specs: ["Length: 13.28 m", "Guests: 11", "Cabins: 1", "Captain: Included", "Port: Marina Botafoch"] },
  { name: "Pardo 50 'Im Off'", price: "From €3.050/day", image: yachtPardo, slug: "pardo-43", specs: ["Length: 16.25 m", "Guests: 11", "Cabins: 2", "Captain: Included", "Port: Marina Botafoch"] },
  { name: "Mangusta 92 \"Drift\"", price: "From €9.799/day", image: yachtMangusta, slug: "mangusta-92", specs: ["Length: 27.5 m", "Guests: 12", "Cabins: 4", "Captain: Included", "Port: Marina Botafoch"] },
];

const residences = [
  { name: "Can Chi", price: "From €7.000/week", image: villaCanchi, specs: ["San Rafael, Ibiza", "Up to 8 guests", "4 Bedrooms", "3 Bathrooms", "Private pool", "Countryside views & large garden", "Central location on the island"] },
  { name: "Casa Elephante", price: "From €15.000/week", image: villaElephante, specs: ["Roca Llisa, Ibiza", "Up to 16 guests", "8 Bedrooms", "8 Bathrooms", "2 Private pools", "Large garden & terraces", "10 min from Ibiza Town"] },
  { name: "Casa Celeste", price: "From €15.000/week", image: villaCeleste, specs: ["Up to 12 guests", "6 Bedrooms", "6 Bathrooms", "450 sqm living space", "Infinity swimming pool", "Panoramic sea views"] },
];

const cars = [
  { name: "Land Rover Defender 110", price: "From €350/day", image: carDefender, slug: "land-rover-defender-110", specs: ["5 passengers", "2.0L engine", "240 HP", "0-100km/h: 9.1 sec", "Top speed: 250km/h"] },
  { name: "Ferrari F8 Spyder", price: "From €1.500/week", image: carFerrari, slug: "ferrari-f8-spyder", specs: ["2 passengers", "3.9L Engine", "720 HP", "0-100km/h: 2.9 sec", "Top speed: 340km/h"] },
  { name: "Bentley Bentayga", price: "From €1.000/day", image: carBentley, slug: "bentley-bentayga", specs: ["5 passengers", "6.0L Engine", "608 HP", "0-100km/h: 4.1 sec", "Top speed: 301 km/h"] },
  { name: "Porsche 911 Carrera 4S", price: "From €450/day", image: carPorsche, slug: "porsche-911-carrera-4s", specs: ["4 passengers", "3.0L engine", "450 HP", "0-100km/h: 3.5 sec", "Top speed: 322km/h"] },
  { name: "Mercedes SL63 AMG", price: "From €550/day", image: null, slug: "mercedes-sl63-amg", specs: ["2 passengers", "4.0L engine", "585 HP", "0-100km/h: 3.6 sec", "Top speed: 317km/h"] },
];

type CatalogueItem = { name: string; price: string; image: string | null; specs: string[]; slug?: string };

/* ─── COMPONENTS ─── */

const CatalogueCard = ({ item, basePath }: { item: CatalogueItem; basePath?: string }) => {
  const linkTo = item.slug && basePath ? `${basePath}/${item.slug}` : undefined;

  const imageBlock = item.image ? (
    <div className="image-architectural aspect-[4/3] overflow-hidden mb-6 cursor-pointer">
      <img
        src={item.image}
        alt={item.name}
        className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.03]"
        loading="lazy"
      />
    </div>
  ) : null;

  return (
    <div className="group min-w-0">
      {linkTo ? (
        <Link to={linkTo}>{imageBlock}</Link>
      ) : (
        imageBlock
      )}
      <h3 className="font-display-medium text-lg md:text-xl text-foreground mb-2 tracking-wide uppercase">
        {item.name}
      </h3>
      <p className="font-label text-bronze mb-4">{item.price}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4">
        {item.specs.map((spec) => (
          <span key={spec} className="font-body text-muted-foreground text-xs italic">{spec}</span>
        ))}
      </div>
      {linkTo ? (
        <Link
          to={linkTo}
          className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px]"
        >
          {`View ${item.name} details`}
        </Link>
      ) : (
        <button
          onClick={() => document.getElementById("ibiza-inquire")?.scrollIntoView({ behavior: "smooth" })}
          className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px]"
        >
          {`Inquire about ${item.name}`}
        </button>
      )}
    </div>
  );
};

const basePaths: Record<string, string> = {
  yachts: "/yachts",
  residences: "/residences",
  drive: "/drive/ibiza",
};

const CatalogueCarousel = ({
  id, label, title, items, viewAllLabel, bgClass = "bg-background",
}: {
  id: string; label: string; title: string; items: CatalogueItem[]; viewAllLabel: string; bgClass?: string;
}) => (
  <section id={id} className={`section-spacing ${bgClass}`}>
    <div className="container-luxury">
      <motion.div
        className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.2, ease }}
      >
        <div>
          <span className="font-label text-bronze block mb-4">{label}</span>
          <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase">{title}</h2>
        </div>
        <Link
          to={id === "yachts" ? "/yachts/ibiza" : id === "residences" ? "/ibiza/residences" : id === "drive" ? "/drive/ibiza" : "#"}
          className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] self-start md:self-auto"
        >
          {viewAllLabel}
        </Link>
      </motion.div>

      <Carousel opts={{ align: "start", loop: false }} className="w-full">
        <CarouselContent className="-ml-6 md:-ml-10">
          {items.map((item) => (
            <CarouselItem key={item.name} className="pl-6 md:pl-10 basis-[85%] md:basis-[45%] lg:basis-[35%]">
              <CatalogueCard item={item} basePath={basePaths[id]} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="flex items-center gap-4 mt-10">
          <CarouselPrevious className="static translate-y-0 h-10 w-10 border-foreground/20 text-foreground hover:bg-foreground/5" />
          <CarouselNext className="static translate-y-0 h-10 w-10 border-foreground/20 text-foreground hover:bg-foreground/5" />
        </div>
      </Carousel>
    </div>
  </section>
);

const serviceLinkMap: Record<string, string> = {
  yachts: "/yachts/ibiza",
  residences: "/ibiza/residences",
  drive: "/drive/ibiza",
  lifestyle: "/ibiza/lifestyle",
  nights: "/ibiza/nights",
};

const ServiceBanner = ({ service, index }: { service: typeof services[0]; index: number }) => {
  const linkTo = serviceLinkMap[service.sectionId];
  return (
    <motion.div
      className="relative h-[50vh] md:h-[60vh] overflow-hidden group"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1.2, delay: 0.1 * index, ease }}
    >
      <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-[3000ms] ease-out group-hover:scale-[1.02]" loading="lazy" />
      <div className="absolute inset-0 bg-foreground/40" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
        <h3 className="font-display text-3xl md:text-5xl text-primary-foreground mb-4 tracking-wide uppercase">{service.title}</h3>
        <p className="font-body text-primary-foreground/70 text-sm md:text-base max-w-xl leading-[1.8] mb-8">{service.description}</p>
        {linkTo ? (
          <Link
            to={linkTo}
            className="font-label text-primary-foreground border-b border-primary-foreground/40 pb-1 hover:border-primary-foreground transition-colors duration-700 text-[10px]"
          >
            {service.cta}
          </Link>
        ) : (
          <button
            onClick={() => document.getElementById(service.sectionId)?.scrollIntoView({ behavior: "smooth" })}
            className="font-label text-primary-foreground border-b border-primary-foreground/40 pb-1 hover:border-primary-foreground transition-colors duration-700 text-[10px]"
          >
            {service.cta}
          </button>
        )}
      </div>
    </motion.div>
  );
};

/* ─── PAGE ─── */

const Ibiza = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Arzani Group Ibiza — Luxury Concierge & Curated Experiences"
        description="Private yachts, villas, prestige cars and lifestyle across Ibiza and Formentera — arranged with discretion."
        path="/ibiza"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Luxury concierge",
          areaServed: "Ibiza, Spain",
          provider: { "@type": "Organization", name: "Arzani Group" },
        }}
      />
      <Navigation />

      {/* Hero — Es Vedrà */}
      <section className="relative min-h-[100vh] flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <video
            src="/videos/ibiza-hero.mov"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={heroImage}
            className="w-full h-full object-cover scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground/50 via-foreground/20 to-foreground/60" />
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-background" />
        </div>
        <div className="relative z-10 text-center px-8 flex flex-col items-center">
          <motion.img src={logoFigure} alt="Arzani Group" className="h-20 md:h-28 mb-6 invert" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6, duration: 1.8, ease }} />
          <motion.h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground tracking-wider uppercase mb-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1.8, ease }}>
            Arzani Group
          </motion.h1>
          <motion.p className="font-label text-primary-foreground/60 tracking-[0.4em] mb-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1.8, ease }}>
            I B I Z A
          </motion.p>
          <motion.p className="font-accent text-primary-foreground/60 text-sm md:text-base italic max-w-md" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1.8, ease }}>
            Secluded coves, iconic venues<br />perfectly arranged.
          </motion.p>
        </div>
        <motion.div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2, duration: 1.5, ease }}>
          <span className="font-label text-primary-foreground/30 text-[8px]">Scroll</span>
          <div className="w-px h-8 bg-primary-foreground/20" />
        </motion.div>
      </section>

      {/* The Arzani Way — Intro */}
      <section className="section-spacing bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 1.4, ease }}>
              <span className="font-label text-bronze block mb-4">The Arzani Way</span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.1] mb-4 tracking-wide uppercase">I B I Z A</h2>
              <div className="divider-subtle mb-8" />
              <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9] mb-6">
                Ibiza moves between serenity and celebration, offering moments of quiet beauty alongside iconic experiences. With Arzani, every part of your stay is seamlessly arranged — from exceptional private residences and yacht charters to luxury vehicles, chauffeur service, and priority reservations across Ibiza and Formentera.
              </p>
              <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9]">
                Designed with discretion and executed with precision, each experience is carefully curated to give you effortless access to the island's most sought-after moments, allowing you to experience Ibiza with complete ease, confidence, and distinction.
              </p>
            </motion.div>
            <motion.div className="overflow-hidden" initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 1.4, delay: 0.15, ease }}>
              <img src={introImage} alt="Luxury villa terrace overlooking Ibiza coastline" className="w-full h-[400px] md:h-[520px] lg:h-[600px] object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Banners */}
      <section>
        {services.map((service, i) => (
          <ServiceBanner key={service.title} service={service} index={i} />
        ))}
      </section>

      {/* Catalogues as swipeable carousels */}
      <CatalogueCarousel id="yachts" label="Our Fleet" title="Arzani Yachts" items={yachts} viewAllLabel="Discover all our yachts" />
      <CatalogueCarousel id="residences" label="Private Stays" title="Arzani Residences" items={residences} viewAllLabel="Discover all our residences" bgClass="bg-secondary/40" />

      {/* CTA Banner */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src={ctaImage} alt="Ibiza sunset" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-foreground/50" />
        </div>
        <div className="relative z-10 container-luxury text-center">
          <motion.h2 className="font-display text-3xl md:text-5xl text-primary-foreground mb-6 tracking-wide uppercase" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1.2, ease }}>
            Let's craft your next holiday
          </motion.h2>
          <motion.p className="font-body text-primary-foreground/70 text-sm md:text-base max-w-lg mx-auto mb-10 leading-[1.8]" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2, duration: 1.2, ease }}>
            From the ideal home to the most unforgettable experiences, tell us the vision and we will organise your ultimate getaway.
          </motion.p>
          <motion.a href="https://wa.me/message/2XEKJ5STR2IZJ1" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-3 border border-primary-foreground/40 text-primary-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-300" initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4, duration: 1, ease }}>
            Get in touch
          </motion.a>
        </div>
      </section>

      <CatalogueCarousel id="drive" label="Prestige Vehicles" title="Arzani Drive" items={cars} viewAllLabel="Discover all our vehicles" />

      {/* Inquiry Section */}
      <section id="ibiza-inquire" className="section-spacing bg-warm-black">
        <div className="container-luxury max-w-2xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1.2, ease }}>
            <h2 className="font-display text-2xl md:text-3xl text-bronze tracking-wide uppercase mb-8">Other Requests?</h2>
            <p className="font-body text-primary-foreground/60 text-sm leading-[1.9] mb-10">
              For bespoke requests beyond our listed services, our team will be pleased to assist.
            </p>
            <a href="https://wa.me/message/2XEKJ5STR2IZJ1" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-3 border border-primary-foreground/30 text-primary-foreground/80 text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-300">
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Ibiza;
